

import { useState, useEffect } from "react"
import axios from "axios"
import { motion, AnimatePresence } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import  Button  from "@/components/ui/button"
import {
  Trash2,
  X,
  Loader2,
  Code,
  FileText,
  ImageIcon,
  Edit,
  Maximize2,
  ChevronDown,
  Sparkles,
  Eye,
  EyeOff,
} from "lucide-react"

export default function SlideFrameManager({ latexCode, onUpdate, onClose }) {
  const [frames, setFrames] = useState([])
  const [plots, setPlots] = useState([])
  const [activeTab, setActiveTab] = useState("frames")
  const [selectedItemIndex, setSelectedItemIndex] = useState(null)
  const [selectedAction, setSelectedAction] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [modifiedLatex, setModifiedLatex] = useState(latexCode)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showRegenerateInput, setShowRegenerateInput] = useState(false)
  const [regenerateRequirement, setRegenerateRequirement] = useState("")

  // Parse LaTeX code to extract frames and Python plots on component mount or when latexCode changes
  useEffect(() => {
    parseLatexCode(latexCode)
    setModifiedLatex(latexCode)
  }, [latexCode])

  // Function to parse LaTeX code and extract frames and Python plots
  const parseLatexCode = (latex) => {
    // Extract frames
    const frameRegex = /\\begin{frame}(.*?)\\end{frame}/gs
    const frameMatches = [...latex.matchAll(frameRegex)]

    // Also look for title page frames
    const titlePageRegex = /\\frame{\\titlepage}/g
    const titlePageMatches = [...latex.matchAll(titlePageRegex)]

    const extractedFrames = frameMatches.map((match, index) => {
      const fullContent = match[0]
      const titleMatch = fullContent.match(/\\frametitle{(.*?)}/)
      const title = titleMatch ? titleMatch[1] : `Slide ${index + 1}`

      return {
        id: `frame-${index}`,
        index,
        title,
        content: fullContent,
        position: match.index,
        length: match[0].length,
      }
    })

    // Add title page frames
    titlePageMatches.forEach((match, i) => {
      extractedFrames.push({
        id: `title-frame-${i}`,
        index: extractedFrames.length,
        title: "Title Page",
        content: match[0],
        position: match.index,
        length: match[0].length,
        isTitlePage: true,
      })
    })

    // Sort frames by their position in the document
    extractedFrames.sort((a, b) => a.position - b.position)

    // Reassign indices after sorting
    extractedFrames.forEach((frame, index) => {
      frame.index = index
    })

    setFrames(extractedFrames)

    // Extract Python code blocks
    const pythonRegex = /```python(.*?)```/gs
    const pythonMatches = [...latex.matchAll(pythonRegex)]

    const extractedPlots = pythonMatches.map((match, index) => {
      const fullCode = match[1].trim()

      // Try to extract plot title or type from the code
      let title = `Plot ${index + 1}`
      const titleComment = fullCode.match(/#\s*Title:\s*(.*?)(\n|$)/)
      if (titleComment) {
        title = titleComment[1].trim()
      } else {
        // Try to guess plot type from matplotlib functions
        if (fullCode.includes("plt.scatter")) title = `Scatter Plot ${index + 1}`
        else if (fullCode.includes("plt.bar")) title = `Bar Chart ${index + 1}`
        else if (fullCode.includes("plt.hist")) title = `Histogram ${index + 1}`
        else if (fullCode.includes("plt.pie")) title = `Pie Chart ${index + 1}`
        else if (fullCode.includes("plt.plot")) title = `Line Plot ${index + 1}`
      }

      return {
        id: `plot-${index}`,
        index,
        title,
        code: fullCode,
        fullMatch: match[0],
        position: match.index,
        length: match[0].length,
      }
    })

    setPlots(extractedPlots)
  }

  // Function to delete a frame
  const deleteFrame = (index) => {
    if (isProcessing) return

    const frameToDelete = frames[index]
    if (!frameToDelete) return

    // Create new LaTeX code without the selected frame
    const newLatex =
      modifiedLatex.substring(0, frameToDelete.position) +
      modifiedLatex.substring(frameToDelete.position + frameToDelete.length)

    setModifiedLatex(newLatex)
    parseLatexCode(newLatex)
    setSelectedItemIndex(null)
    setSelectedAction(null)

    // Update the parent component with the modified LaTeX
    onUpdate(newLatex)
  }

  // Function to delete a plot
  const deletePlot = (index) => {
    if (isProcessing) return

    const plotToDelete = plots[index]
    if (!plotToDelete) return

    // Create new LaTeX code without the selected plot
    const newLatex =
      modifiedLatex.substring(0, plotToDelete.position) +
      modifiedLatex.substring(plotToDelete.position + plotToDelete.length)

    setModifiedLatex(newLatex)
    parseLatexCode(newLatex)
    setSelectedItemIndex(null)
    setSelectedAction(null)

    // Update the parent component with the modified LaTeX
    onUpdate(newLatex)
  }

  // Function to regenerate a frame using AI
  const redoFrame = async (index, requirement = "") => {
    if (isProcessing) return

    const frameToRedo = frames[index]
    if (!frameToRedo) return

    setIsProcessing(true)

    try {
      // Skip regeneration for title pages
      if (frameToRedo.isTitlePage) {
        throw new Error("Title pages cannot be regenerated")
      }

      // Extract the frame title to maintain context
      const titleMatch = frameToRedo.content.match(/\\frametitle{(.*?)}/)
      const frameTitle = titleMatch ? titleMatch[1] : `Slide ${index + 1}`

      // Call AI service to regenerate the frame
      const response = await axios.post("/api/regenerate-frame", {
        frameTitle,
        originalContent: frameToRedo.content,
        slideContext: modifiedLatex, // Send full context for better regeneration
        requirement: requirement, // Add the user's requirement
      })

      if (response.data && response.data.regeneratedFrame) {
        // Replace the old frame with the new one
        const newLatex =
          modifiedLatex.substring(0, frameToRedo.position) +
          response.data.regeneratedFrame +
          modifiedLatex.substring(frameToRedo.position + frameToRedo.length)

        setModifiedLatex(newLatex)
        parseLatexCode(newLatex)

        // Update the parent component with the modified LaTeX
        onUpdate(newLatex)
      } else {
        throw new Error("Failed to regenerate frame")
      }
    } catch (error) {
      console.error("Error regenerating frame:", error)
      // Show error notification or handle error
    } finally {
      setIsProcessing(false)
      setSelectedItemIndex(null)
      setSelectedAction(null)
      setShowRegenerateInput(false)
      setRegenerateRequirement("")
    }
  }

  // Function to regenerate a plot using AI
  const redoPlot = async (index, requirement = "") => {
    if (isProcessing) return

    const plotToRedo = plots[index]
    if (!plotToRedo) return

    setIsProcessing(true)

    try {
      // Find the frame that might contain this plot for context
      let associatedFrameContent = ""
      for (const frame of frames) {
        if (frame.content.includes(plotToRedo.fullMatch)) {
          associatedFrameContent = frame.content
          break
        }
      }

      // Call AI service to regenerate the plot
      const response = await axios.post("/api/regenerate-plot", {
        plotTitle: plotToRedo.title,
        originalCode: plotToRedo.code,
        frameContext: associatedFrameContent,
        slideContext: modifiedLatex, // Send full context for better regeneration
        requirement: requirement, // Add the user's requirement
      })

      if (response.data && response.data.regeneratedPlotCode) {
        // Wrap the new code in Python markdown
        const newPlotBlock = "```python\n" + response.data.regeneratedPlotCode + "\n```"

        // Replace the old plot with the new one
        const newLatex =
          modifiedLatex.substring(0, plotToRedo.position) +
          newPlotBlock +
          modifiedLatex.substring(plotToRedo.position + plotToRedo.length)

        setModifiedLatex(newLatex)
        parseLatexCode(newLatex)

        // Update the parent component with the modified LaTeX
        onUpdate(newLatex)
      } else {
        throw new Error("Failed to regenerate plot")
      }
    } catch (error) {
      console.error("Error regenerating plot:", error)
      // Show error notification or handle error
    } finally {
      setIsProcessing(false)
      setSelectedItemIndex(null)
      setSelectedAction(null)
      setShowRegenerateInput(false)
      setRegenerateRequirement("")
    }
  }

  // Render a preview of a frame (simplified LaTeX to HTML)
  const renderFramePreview = (frameContent) => {
    // Handle title page specially
    if (frameContent.includes("\\titlepage")) {
      return (
        <div className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm overflow-auto max-h-80 text-center">
          <h2 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4">Title Page</h2>
          <p className="text-gray-600 dark:text-gray-400">This is the presentation title page</p>
        </div>
      )
    }

    // This is a very simplified preview - in a real app you might use a LaTeX renderer
    // or generate an image preview on the server
    const preview = frameContent
      .replace(/\\begin{frame}.*?\n/, "")
      .replace(/\\end{frame}/, "")
      .replace(/\\frametitle{(.*?)}/, "<h3 class='text-lg font-bold text-blue-700 dark:text-blue-300 mb-3'>$1</h3>")
      .replace(/\\begin{itemize}/, "<ul class='list-disc pl-5 space-y-2'>")
      .replace(/\\end{itemize}/, "</ul>")
      .replace(/\\item\s(.*?)(?=\\item|\\end{itemize}|$)/gs, "<li class='text-gray-700 dark:text-gray-300'>$1</li>")
      .replace(/\\textbf{(.*?)}/g, "<strong class='font-bold'>$1</strong>")
      .replace(/\\emph{(.*?)}/g, "<em class='italic'>$1</em>")
      .replace(/\\\\/g, "<br>")

    return (
      <div
        className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm overflow-auto max-h-80"
        dangerouslySetInnerHTML={{ __html: preview }}
      />
    )
  }

  // Render a preview of a Python plot code
  const renderPlotPreview = (plotCode) => {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm overflow-auto max-h-80">
        <pre className="text-sm text-gray-800 dark:text-gray-300 whitespace-pre-wrap font-mono">{plotCode}</pre>
      </div>
    )
  }

  // Handle action selection
  const handleActionSelect = (action) => {
    setSelectedAction(action)

    if (action === "delete") {
      if (activeTab === "frames" && selectedItemIndex !== null) {
        deleteFrame(selectedItemIndex)
      } else if (activeTab === "plots" && selectedItemIndex !== null) {
        deletePlot(selectedItemIndex)
      }
    } else if (action === "redo") {
      // Instead of immediately regenerating, show the input box
      setShowRegenerateInput(true)
    } else if (action === "preview") {
      setShowPreview(!showPreview)
    }
  }

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <motion.div
      className={`fixed ${isExpanded ? "inset-0 z-50 flex items-center justify-center bg-black/40" : "bottom-0 right-6 z-50 "}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
    >
      <Card
        className={`bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 ${isExpanded ? "w-[900px] max-w-[90vw]" : "w-[500px]"}`}
        style={{ border: "none" }}
      >
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-5 flex justify-between items-center">
          <div className="flex items-center gap-5">
            <div className="bg-white/20 p-2 rounded-xl">
              <Edit className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Slide Editor</h3>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={toggleExpand}
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-xl text-white hover:bg-white/20"
            >
              <Maximize2 className="h-5 w-5" />
              <span className="sr-only">{isExpanded ? "Minimize" : "Maximize"}</span>
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0 rounded-xl text-white hover:bg-white/20"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </div>

        <CardContent className="p-6 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
          <div className="space-y-6">
            {/* Step 1: Choose between frames and plots */}
            <div>
              <label className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-3 block">
                Step 1: Select content type
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  className={`relative overflow-hidden group rounded-2xl border-0 py-5 px-4 flex items-center justify-center transition-all duration-300 ${
                    activeTab === "frames"
                      ? "bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-md hover:shadow-lg hover:shadow-blue-500/10"
                  }`}
                  onClick={() => {
                    setActiveTab("frames")
                    setSelectedItemIndex(null)
                    setSelectedAction(null)
                    setShowPreview(false)
                    setShowRegenerateInput(false)
                  }}
                >
                  <span
                    className={`absolute inset-0 w-full h-full ${activeTab === "frames" ? "bg-gradient-to-r from-blue-500/0 via-white/10 to-blue-500/0" : ""} transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000`}
                  ></span>
                  <span className="relative text-base font-medium flex items-center">
                    <FileText className="h-5 w-5 mr-3" />
                    Slides ({frames.length})
                  </span>
                </button>
                <button
                  className={`relative overflow-hidden group rounded-2xl border-0 py-5 px-4 flex items-center justify-center transition-all duration-300 ${
                    activeTab === "plots"
                      ? "bg-gradient-to-br from-purple-500 to-purple-700 text-white shadow-lg shadow-purple-500/30"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-md hover:shadow-lg hover:shadow-purple-500/10"
                  }`}
                  onClick={() => {
                    setActiveTab("plots")
                    setSelectedItemIndex(null)
                    setSelectedAction(null)
                    setShowPreview(false)
                    setShowRegenerateInput(false)
                  }}
                >
                  <span
                    className={`absolute inset-0 w-full h-full ${activeTab === "plots" ? "bg-gradient-to-r from-purple-500/0 via-white/10 to-purple-500/0" : ""} transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000`}
                  ></span>
                  <span className="relative text-base font-medium flex items-center">
                    <ImageIcon className="h-5 w-5 mr-3" />
                    Plots ({plots.length})
                  </span>
                </button>
              </div>
            </div>

            {/* Step 2: Select specific item */}
            <div>
              <label className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-3 block">
                Step 2: Select item number
              </label>
              <div className="relative">
                <select
                  className="w-full rounded-2xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 py-4 text-base shadow-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none cursor-pointer transition-all duration-200 hover:border-blue-400 dark:hover:border-blue-500"
                  value={selectedItemIndex !== null ? selectedItemIndex : ""}
                  onChange={(e) => {
                    const value = e.target.value
                    setSelectedItemIndex(value === "" ? null : Number.parseInt(value))
                    setSelectedAction(null)
                    setShowPreview(false)
                    setShowRegenerateInput(false)
                  }}
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                    backgroundPosition: "right 1.5rem center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "1.5em 1.5em",
                    paddingRight: "3.5rem",
                  }}
                >
                  <option value="">Select a {activeTab === "frames" ? "slide" : "plot"}</option>
                  {activeTab === "frames"
                    ? frames.map((frame, index) => (
                        <option key={frame.id} value={index}>
                          {frame.isTitlePage
                            ? "Title Page"
                            : `Slide ${index + 1}: ${frame.title.length > 30 ? frame.title.substring(0, 30) + "..." : frame.title}`}
                        </option>
                      ))
                    : plots.map((plot, index) => (
                        <option key={plot.id} value={index}>
                          Plot {index + 1}: {plot.title.length > 30 ? plot.title.substring(0, 30) + "..." : plot.title}
                        </option>
                      ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-5 text-gray-500">
                  <ChevronDown
                    className={`h-6 w-6 transition-transform duration-300 ${selectedItemIndex !== null ? "rotate-180" : ""}`}
                  />
                </div>
                <div
                  className={`absolute -bottom-1 left-0 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500 ${selectedItemIndex !== null ? "w-full opacity-100" : "w-0 opacity-0"}`}
                ></div>
              </div>
            </div>

            {/* Step 3: Choose action */}
            {selectedItemIndex !== null && (
              <div>
                <label className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-3 block">
                  Step 3: Select action
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    className={`relative overflow-hidden group rounded-2xl py-4 px-5 flex items-center justify-center transition-all duration-300 ${
                      showPreview
                        ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/10"
                    }`}
                    onClick={() => handleActionSelect("preview")}
                  >
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-500/0 via-white/10 to-blue-500/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>
                    <span className="relative text-base font-medium flex items-center">
                      {showPreview ? (
                        <>
                          <EyeOff className="h-5 w-5 mr-2" />
                          Hide
                        </>
                      ) : (
                        <>
                          <Eye className="h-5 w-5 mr-2" />
                          Preview
                        </>
                      )}
                    </span>
                  </button>
                  <button
                    className={`relative overflow-hidden group rounded-2xl py-4 px-5 flex items-center justify-center transition-all duration-300 ${
                      frames[selectedItemIndex]?.isTitlePage && activeTab === "frames"
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-800/30 hover:border-red-400 hover:shadow-lg hover:shadow-red-500/10"
                    }`}
                    onClick={() => handleActionSelect("delete")}
                    disabled={isProcessing || (frames[selectedItemIndex]?.isTitlePage && activeTab === "frames")}
                  >
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-red-500/0 via-white/10 to-red-500/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>
                    <span className="relative text-base font-medium flex items-center">
                      <Trash2 className="h-5 w-5 mr-2" />
                      Delete
                    </span>
                  </button>
                  <button
                    className={`relative overflow-hidden group rounded-2xl py-4 px-5 flex items-center justify-center transition-all duration-300 ${
                      frames[selectedItemIndex]?.isTitlePage && activeTab === "frames"
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-800/30 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/10"
                    }`}
                    onClick={() => handleActionSelect("redo")}
                    disabled={isProcessing || (frames[selectedItemIndex]?.isTitlePage && activeTab === "frames")}
                  >
                    <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-500/0 via-white/10 to-blue-500/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>
                    <span className="relative text-base font-medium flex items-center">
                      {isProcessing ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="h-5 w-5 mr-2" />
                      )}
                      Regenerate
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Regenerate Input Box */}
            {selectedItemIndex !== null && showRegenerateInput && (
              <AnimatePresence>
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden mt-6"
                >
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-lg border border-gray-200 dark:border-gray-700">
                    <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Regeneration Requirements (Optional)
                    </label>
                    <textarea
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder={`Enter specific requirements for regenerating this ${activeTab === "frames" ? "slide" : "plot"}...`}
                      rows={3}
                      value={regenerateRequirement}
                      onChange={(e) => setRegenerateRequirement(e.target.value)}
                    />
                    <div className="flex justify-end gap-3 mt-4">
                      <button
                        className="px-4 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => {
                          setShowRegenerateInput(false)
                          setRegenerateRequirement("")
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200"
                        onClick={() => {
                          if (activeTab === "frames") {
                            redoFrame(selectedItemIndex, regenerateRequirement)
                          } else {
                            redoPlot(selectedItemIndex, regenerateRequirement)
                          }
                        }}
                      >
                        <span className="flex items-center">
                          <Sparkles className="h-4 w-4 mr-2" />
                          Regenerate
                        </span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {/* Preview content */}
            {selectedItemIndex !== null && showPreview && (
              <AnimatePresence>
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden mt-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`p-2 rounded-xl ${activeTab === "frames" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" : "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"}`}
                    >
                      {activeTab === "frames" ? <FileText className="h-5 w-5" /> : <Code className="h-5 w-5" />}
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                      {activeTab === "frames"
                        ? frames[selectedItemIndex].isTitlePage
                          ? "Title Page"
                          : `Slide ${selectedItemIndex + 1}: ${frames[selectedItemIndex].title}`
                        : `Plot ${selectedItemIndex + 1}: ${plots[selectedItemIndex].title}`}
                    </h4>
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    {activeTab === "frames"
                      ? renderFramePreview(frames[selectedItemIndex].content)
                      : renderPlotPreview(plots[selectedItemIndex].code)}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {/* Status message */}
            {isProcessing && (
              <div className="text-base text-center text-gray-500 dark:text-gray-400 animate-pulse mt-6 bg-gray-50 dark:bg-gray-800/50 py-5 px-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="flex items-center justify-center">
                  <div className="relative">
                    <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-blue-500 animate-spin"></div>
                    <div
                      className="absolute top-0 left-0 h-12 w-12 rounded-full border-t-2 border-b-2 border-purple-500 animate-spin"
                      style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
                    ></div>
                  </div>
                </div>
                <p className="mt-3 font-medium">Generating amazing content for you...</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

